import { RouteComponentProps } from '@reach/router';
import { LessonPlan, LessonsApi, PresentationResource } from 'api/lessonsApi';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import { LessonsRouteParams, useLessonsPage } from 'components/lessons/lessonsHelpers';
import Page from 'components/shared/Page';
import React, { useRef, useState } from 'react';
import SwiperCore, { Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';

SwiperCore.use([Keyboard, Navigation]);

type Props = RouteComponentProps<LessonsRouteParams>;

const slide = (resource: PresentationResource) => {
  if (!resource.videoURL) return null;

  switch (resource.resourceType) {
    case 'Image':
      return (
        <div className="PresentationImage">
          {resource.additionalAudioURL && <audio controls src={resource.additionalAudioURL} />}
          <img src={resource.videoURL} alt={resource.name} />
        </div>
      );
    case 'Video':
      return (
        <video
          controls
          disablePictureInPicture
          controlsList="nodownload noplaybackrate"
          src={resource.videoURL}
        />
      );
    case 'Audio':
      return <audio controls src={resource.videoURL} />;
    default:
      return null;
  }
};

const LessonPresentation: React.FC<Props> = (props) => {
  const result = useLessonsPage<LessonPlan>(props, (query, lessonNumber) =>
    LessonsApi.getPresentation(query, lessonNumber)
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const [bFullScreen, setBFullScreen] = useState(true);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }
  if (result.details.presentationResources.length === 0) {
    return <LessonPageStatus status="notFound" />;
  }

  // Media on the slide left behind stops and goes back to the start
  const stopMedia = () => {
    containerRef.current?.querySelectorAll<HTMLMediaElement>('video, audio').forEach((media) => {
      media.pause();
      media.currentTime = 0;
    });
  };

  return (
    <Page bypassPageAccess iFramePage>
      <div ref={containerRef} className={`PresentationBlock${bFullScreen ? ' FullScreen' : ''}`}>
        <Swiper
          speed={0}
          autoHeight={true}
          simulateTouch={false}
          keyboard={{ enabled: true }}
          navigation={{ prevEl: '.PresentationPrev', nextEl: '.PresentationNext' }}
          onSlideChange={stopMedia}
        >
          {result.details.presentationResources.map((resource) => (
            <SwiperSlide key={resource.resourcesId}>{slide(resource)}</SwiperSlide>
          ))}
        </Swiper>
        <div className="PresentationControls">
          <button type="button" className="PresentationPrev" aria-label="Previous" />
          <button type="button" className="PresentationNext" aria-label="Next" />
          <button
            type="button"
            className="PresentationFullScreen"
            aria-label="Full screen"
            onClick={() => setBFullScreen((previous) => !previous)}
          />
        </div>
      </div>
    </Page>
  );
};

export default LessonPresentation;
